import React from 'react';
import { useObservableState } from 'observable-hooks';
import { firstValueFrom, map, type Observable } from 'rxjs';
import type { AnySchema } from 'yup';
import { AttributeValueType } from '@/graphql/types';
import classNames from '../../../../../utils/classNames';
import { BaseReportElement } from '../BaseReportElement';
import { createOptionalInsertionPresets } from '../insertionPresetUtils';
import {
    createBlankImageGalleryDefinition,
    createRuntimeImageGalleryInsertionPresets,
} from './presets';
import {
    buildImageGallerySettingsYupSchema,
    normalizeImageGallerySettings,
} from './settingsYupSchema';
import { createDefaultImageGallerySettings, type ImageGallerySettings } from './contracts';
import {
    createContainCoverChoiceOptions,
    createRuntimeBindingGroupField,
    resolveRuntimeBindingSettingsFormSchema,
    renderRuntimeBindingPlaceholder,
    withRuntimeBindingArgChoiceOptions,
} from '../runtimeBindingFormUtils';
import { resolveAttributeArgOptions } from '../../runtime/dataServices/attributes/argOptions';
import type {
    BuildStructureSettingsYupSchemaHelpers,
    ContextForKind,
    EditorRenderRuntime,
    ElementContentRenderResult,
    ElementSettingsFormSchema,
    RenderContextKind,
    RenderRuntime,
    ResolveElementSettingsFormSchemaArgs,
} from '../types';
import type { RuntimeImageAsset } from '../../runtime/dataServices/contracts';
import {
    isRecord,
    normalizeNonEmptyString,
    normalizeString,
} from '../../runtime/dataServices/validationPrimitives';

function parseImageListOrThrow(value: unknown): RuntimeImageAsset[] {
    if (!Array.isArray(value)) {
        throw new Error('Expected "image_gallery.binding.value" to be an array.');
    }

    return value.map((entry, index) => {
        if (!isRecord(entry)) {
            throw new Error(
                `Expected "image_gallery.binding.value[${index}]" to be an image asset object.`
            );
        }

        return {
            url: normalizeNonEmptyString(entry.url, `image_gallery.binding.value[${index}].url`),
            caption: normalizeString(
                entry.caption,
                `image_gallery.binding.value[${index}].caption`
            ),
        };
    });
}

function chunkByPageSize(
    imageAssets: RuntimeImageAsset[],
    imagesPerPage: 2 | 4
): RuntimeImageAsset[][] {
    const chunks: RuntimeImageAsset[][] = [];

    for (let index = 0; index < imageAssets.length; index += imagesPerPage) {
        chunks.push(imageAssets.slice(index, index + imagesPerPage));
    }

    return chunks;
}

interface RuntimeImageGalleryRendererProps {
    imageAssets$: Observable<RuntimeImageAsset[]>;
    settings: ImageGallerySettings;
    fallbackLabel: string;
    showMissingImagePlaceholder: boolean;
}

function RuntimeImageGalleryRenderer({
    imageAssets$,
    settings,
    fallbackLabel,
    showMissingImagePlaceholder,
}: RuntimeImageGalleryRendererProps): JSX.Element | null {
    const imageAssets = useObservableState(imageAssets$, []);

    if (!imageAssets || !imageAssets.length) {
        return showMissingImagePlaceholder ? renderRuntimeBindingPlaceholder(fallbackLabel) : null;
    }

    const chunks = chunkByPageSize(imageAssets, settings.imagesPerPage);
    const pageGridClassName =
        settings.imagesPerPage === 2 ? 'grid-cols-1 grid-rows-2' : 'grid-cols-2 grid-rows-2';

    return (
        <div className="w-full">
            {chunks.map((chunk, pageIndex) => (
                <React.Fragment key={`gallery-page-${pageIndex}`}>
                    {pageIndex > 0 ? (
                        <div
                            aria-hidden
                            className="image-gallery-page-break h-0"
                            style={{ breakBefore: 'page', pageBreakBefore: 'always' }}
                        />
                    ) : null}
                    <section
                        className="image-gallery-page w-full break-inside-avoid"
                        style={{
                            height: 'var(--rb-flowable-container-height, var(--rb-page-printable-height, 100%))',
                        }}
                    >
                        <div className={classNames('grid h-full w-full gap-2', pageGridClassName)}>
                            {chunk.map((imageAsset, imageIndex) => (
                                <figure
                                    key={`${pageIndex}:${imageIndex}:${imageAsset.url}`}
                                    className="m-0 flex h-full min-h-0 break-inside-avoid flex-col overflow-hidden"
                                >
                                    <img
                                        src={imageAsset.url}
                                        alt={`${gettext('ELEMENT_IMAGE_GALLERY_IMAGE_ALT_LABEL')} ${
                                            pageIndex * settings.imagesPerPage + imageIndex + 1
                                        }`}
                                        className={classNames(
                                            'min-h-0 w-full flex-1 overflow-hidden',
                                            settings.fit === 'cover'
                                                ? 'object-cover'
                                                : 'object-contain'
                                        )}
                                    />
                                    {imageAsset.caption ? (
                                        <figcaption className="mt-1 flex-none text-xs leading-tight text-gray-600">
                                            {imageAsset.caption}
                                        </figcaption>
                                    ) : null}
                                </figure>
                            ))}
                        </div>
                    </section>
                </React.Fragment>
            ))}
        </div>
    );
}

export class ImageGalleryElement extends BaseReportElement<
    RenderContextKind,
    ImageGallerySettings
> {
    static readonly elementType = 'image_gallery';

    static readonly layout = { flow: 'flow' } as const;

    static readonly compatibleContextKinds = ['project', 'measuring-point'] as const;

    static get insertion() {
        const presets = createRuntimeImageGalleryInsertionPresets();

        return {
            label: gettext('ELEMENT_IMAGE_GALLERY_LABEL'),
            supportedSlots: ['child-list'] as const,
            createDefinition: createBlankImageGalleryDefinition,
            ...createOptionalInsertionPresets(presets),
        };
    }

    static get settingsFormSchema(): ElementSettingsFormSchema {
        const defaults = createDefaultImageGallerySettings();

        return {
            fields: [
                createRuntimeBindingGroupField({
                    includeArg: true,
                    validateLinkOptions: false,
                }),
                {
                    name: 'imagesPerPage',
                    label: gettext('ELEMENT_IMAGE_GALLERY_IMAGES_PER_PAGE_LABEL'),
                    type: 'choice',
                    required: true,
                    options: [
                        ['2', gettext('ELEMENT_IMAGE_GALLERY_IMAGES_PER_PAGE_TWO_LABEL')],
                        ['4', gettext('ELEMENT_IMAGE_GALLERY_IMAGES_PER_PAGE_FOUR_LABEL')],
                    ],
                    default: String(defaults.imagesPerPage),
                },
                {
                    name: 'fit',
                    label: gettext('FIT'),
                    type: 'choice',
                    required: true,
                    options: createContainCoverChoiceOptions(),
                    default: defaults.fit,
                },
            ],
        };
    }

    static async resolveSettingsFormSchema({
        contextKind,
        context,
        client,
        currentSettings,
    }: ResolveElementSettingsFormSchemaArgs): Promise<ElementSettingsFormSchema> {
        const settingsFormSchema = resolveRuntimeBindingSettingsFormSchema({
            contextKind,
            settingsFormSchema: ImageGalleryElement.settingsFormSchema,
            includeLinksRequiringArg: true,
            valueKind: 'image-list',
        });

        const argOptions = await resolveAttributeArgOptions({
            client,
            contextKind,
            context,
            valueType: AttributeValueType.ImageList,
            currentArg:
                isRecord(currentSettings.binding) && typeof currentSettings.binding.arg === 'string'
                    ? currentSettings.binding.arg
                    : undefined,
        });

        return withRuntimeBindingArgChoiceOptions(settingsFormSchema, argOptions);
    }

    static buildStructureSettingsYupSchema(
        _helpers: BuildStructureSettingsYupSchemaHelpers
    ): AnySchema {
        return buildImageGallerySettingsYupSchema();
    }

    protected normalizeSettings(settings: ImageGallerySettings): ImageGallerySettings {
        ImageGalleryElement.validateSettings(settings);
        return normalizeImageGallerySettings(settings);
    }

    protected getRuntimeBindings() {
        return this.settings.binding ? [this.settings.binding] : [];
    }

    protected toCompatibleReactNode(
        ctx: ContextForKind<RenderContextKind>,
        runtime: RenderRuntime
    ): ElementContentRenderResult {
        const { binding } = this.settings;
        if (!binding) {
            return {
                node: renderRuntimeBindingPlaceholder(
                    gettext('ELEMENT_IMAGE_GALLERY_PLACEHOLDER_BINDING_REQUIRED')
                ),
                ready: Promise.resolve(),
                hasRenderableContent: Promise.resolve(true),
            };
        }

        const imageAssets$ = runtime
            .observeBinding(binding, { context: ctx })
            .pipe(map((value) => parseImageListOrThrow(value)));

        return {
            node: (
                <RuntimeImageGalleryRenderer
                    imageAssets$={imageAssets$}
                    settings={this.settings}
                    fallbackLabel={gettext('ELEMENT_IMAGE_GALLERY_PLACEHOLDER_NO_IMAGE')}
                    showMissingImagePlaceholder={runtime.renderSurface !== 'export'}
                />
            ),
            ready: firstValueFrom(imageAssets$).then(() => undefined),
            hasRenderableContent: Promise.resolve(true),
        };
    }

    protected getEditorFrameContainerClassName(_runtime: EditorRenderRuntime): string {
        return 'relative w-full max-w-full';
    }
}
